import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { farmvestService } from '../../../services/farmvest_api';
import { FarmvestEmployee } from '../../../types/farmvest';
import { formatApiError } from '../../../services/apiUtils';

const mapEmployeeData = (item: any, index: number) => {
    // Normalize status
    const rawStatus = item.is_active !== undefined ? item.is_active : item.active_status;
    const isActive = Number(rawStatus) ? 1 : 0;

    return {
        ...item,
        id: item.id || item.user_id || item.employee_id || item.emp_id || item.employee_code || item.investor_id || item.user?.id || item.data?.id || index,
        first_name: item.first_name || '',
        last_name: item.last_name || '',
        full_name: (item.full_name || item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || '').trim(),
        email: item.email || '',
        mobile: item.mobile || item.phone_number || '',
        phone_number: item.mobile || item.phone_number || '',
        roles: Array.isArray(item.roles) ? item.roles.flatMap((r: any) => typeof r === 'string' ? r.split(',').map((s: string) => s.trim()) : r) : (typeof item.roles === 'string' ? item.roles.split(',').map((r: string) => r.trim()) : (item.role ? [item.role] : (item.role_name ? [item.role_name] : ['Investor']))),
        is_active: isActive,
        active_status: isActive,
        joining_date: item.created_at || item.joining_date || '',
        farm_name: item.farm_name || item.farm?.farm_name || item.farm?.name || (item.farm_details ? item.farm_details.farm_name : '') || item.farm_id || item.farm?.id || '',
        shed_name: item.shed_name || item.shed?.shed_name || item.shed?.name || (item.shed_details ? item.shed_details.shed_name : '') || item.shed_id || item.shed?.id || '',
        location: item.location || item.location_name || item.farm?.location || item.farm?.location_name || item.farm_location || (item.farm_details ? (item.farm_details.location || item.farm_details.location_name) : '') || '',
        last_login: item.last_login || null,
        created_at_timestamp: new Date(item.created_at || item.joining_date || 0).getTime(),
        role_status: item.role_status || (() => {
            if (Array.isArray(item.roles_detail) && item.roles_detail.some((rd: any) => rd.is_active !== undefined)) {
                return Object.fromEntries(item.roles_detail.map((rd: any) => [String(rd.role).toUpperCase(), !!rd.is_active]));
            }
            return undefined;
        })(),
    };
};

export const fetchEmployees = createAsyncThunk(
    'farmvestEmployees/fetchEmployees',
    async (params: { role?: string; is_active?: number; sort_by?: number; page?: number; size?: number; search?: string; farm_id?: number } | undefined, { rejectWithValue }) => {
        try {
            const response = await farmvestService.getEmployees(params);

            let rawData: any[] = [];

            // Extract the array from various possible structures
            if (Array.isArray(response)) {
                rawData = response;
            } else if (response && Array.isArray(response.data)) {
                rawData = response.data;
            } else if (response && (response.users || response.employees)) {
                rawData = response.users || response.employees;
            } else if (response && response.data && (Array.isArray(response.data.employees) || Array.isArray(response.data.users))) {
                rawData = response.data.employees || response.data.users;
            } else {
                // Fallback: check if status is 200 and data exists but hasn't been caught yet
                if (response && response.status === 200 && Array.isArray(response.data)) {
                    rawData = response.data;
                }
            }

            if (rawData.length > 0 || (response && (response.status === 'success' || response.status === 200))) {
                // Map the data to match FarmvestEmployee interface
                const mappedData = rawData.map(mapEmployeeData);

                // Extract total count if available
                const pagination = response.pagination || response.data?.pagination;
                const totalCount =
                    pagination?.total_items ||
                    pagination?.total_count ||
                    response.total_users ||
                    response.total ||
                    response.count ||
                    response.total_count ||
                    rawData.length;

                // Extract counts if available in response
                const statusCounts = (response.active_count !== undefined && response.inactive_count !== undefined)
                    ? { active: response.active_count, inactive: response.inactive_count }
                    : (response.status_counts || response.data?.status_counts || null);

                const roleCounts = response.role_counts || response.data?.role_counts || null;

                return {
                    employees: mappedData,
                    totalCount,
                    statusCounts,
                    roleCounts,
                    page: params?.page || 1
                };
            }

            return rejectWithValue(typeof response?.message === 'object' ? JSON.stringify(response.message) : (response?.message || 'Failed to fetch employees'));
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to fetch employees'));
        }
    }
);

export const createEmployee = createAsyncThunk(
    'farmvestEmployees/createEmployee',
    async (employeeData: any, { rejectWithValue }) => {
        try {
            const response = await farmvestService.createEmployee(employeeData);
            return response;
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to create employee'));
        }
    }
);

export const deleteEmployee = createAsyncThunk(
    'farmvestEmployees/deleteEmployee',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await farmvestService.deleteEmployee(id);
            return response;
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to delete employee'));
        }
    }
);




export const updateEmployee = createAsyncThunk(
    'farmvestEmployees/updateEmployee',
    async (payload: { user_id: number; first_name?: string; last_name?: string; email?: string; is_active?: boolean; role?: string; farm_id?: number; shed_id?: number; profile_image_url?: string; }, { rejectWithValue }) => {
        try {
            const response = await farmvestService.updateEmployee(payload);
            return response;
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to update employee'));
        }
    }
);

export const updateEmployeeStatus = createAsyncThunk(
    'farmvestEmployees/updateEmployeeStatus',
    async ({ mobile, role, status, remarks }: { mobile: string; role: string; status: boolean; remarks?: string }, { rejectWithValue }) => {
        try {
            const data = await farmvestService.toggleRoleStatus(mobile, role, status, remarks);
            return { mobile, role, status, remarks, role_status: data?.user?.role_status as Record<string, boolean> | undefined };
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to update employee status'));
        }
    }
);

export const fetchRoleCounts = createAsyncThunk(
    'farmvestEmployees/fetchRoleCounts',
    async (_, { rejectWithValue }) => {
        try {
            // Fetch with size=1 — we only need the metadata (role_counts, active_count, inactive_count)
            // that the backend returns in every response, not the full employee list.
            const response = await farmvestService.getEmployees({ size: 1, page: 1 });

            let allEmployees: any[] = [];

            if (Array.isArray(response)) {
                allEmployees = response;
            } else if (response && Array.isArray(response.data)) {
                allEmployees = response.data;
            } else if (response && (response.users || response.employees)) {
                allEmployees = response.users || response.employees;
            } else if (response && response.data && (Array.isArray(response.data.employees) || Array.isArray(response.data.users))) {
                allEmployees = response.data.employees || response.data.users;
            }

            // Extract counts directly from the Backend API response
            const statusCounts = (response.active_count !== undefined && response.inactive_count !== undefined)
                ? { active: response.active_count, inactive: response.inactive_count }
                : (response.status_counts || response.data?.status_counts || { active: 0, inactive: 0 });

            const roleCounts = response.role_counts || response.data?.role_counts || {};

            // Map allEmployees to follow the same structure as fetchEmployees for consistency
            const mappedEmployees = allEmployees.map(mapEmployeeData);

            return { roleCounts, statusCounts, allEmployees: mappedEmployees };
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to fetch stats'));
        }
    }
);

interface EmployeesState {
    employees: FarmvestEmployee[];
    allEmployees: FarmvestEmployee[];
    totalCount: number;
    globalTotalCount: number;
    loading: boolean;
    roleCountsLoading: boolean;
    createLoading: boolean;
    deleteLoading: boolean;
    updateLoading: boolean;
    error: string | null;
    successMessage: string | null;
    roleCounts: Record<string, number>;
    statusCounts: { active: number; inactive: number };
    loadedPage: number;
    updateStatusLoading: string | null;
}

const initialState: EmployeesState = {
    employees: [],
    allEmployees: [],
    totalCount: 0,
    globalTotalCount: 0,
    loading: false,
    roleCountsLoading: false,
    createLoading: false,
    deleteLoading: false,
    updateLoading: false,
    error: null,
    successMessage: null,
    roleCounts: {},
    statusCounts: { active: 0, inactive: 0 },
    loadedPage: 1,
    updateStatusLoading: null,
};

const employeesSlice = createSlice({
    name: 'farmvestEmployees',
    initialState,
    reducers: {
        setEmployees: (state, action: PayloadAction<FarmvestEmployee[]>) => {
            state.employees = action.payload;
        },
        clearMessages: (state) => {
            state.error = null;
            state.successMessage = null;
        },
        resetForRefresh: (state) => {
            state.employees = [];
            state.allEmployees = [];
            state.totalCount = 0;
        },
        patchEmployeeScreenAccess: (state, action: PayloadAction<{ userId: number; screenAccess: Record<string, string> }>) => {
            const { userId, screenAccess } = action.payload;
            const emp = state.employees.find((e: any) => (e.user_id || e.id) === userId);
            if (emp) (emp as any).screen_access = screenAccess;
            const empAll = state.allEmployees.find((e: any) => (e.user_id || e.id) === userId);
            if (empAll) (empAll as any).screen_access = screenAccess;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchRoleCounts.fulfilled, (state, action) => {
                state.roleCounts = action.payload.roleCounts;
                state.statusCounts = action.payload.statusCounts;
                state.allEmployees = action.payload.allEmployees || [];
                state.globalTotalCount = (action.payload.allEmployees || []).length;
            })
        builder
            .addCase(fetchEmployees.pending, (state) => {
                state.loading = true;
                state.employees = []; // Clear stale data to show loading skeleton
                state.error = null;
            })
            .addCase(fetchEmployees.fulfilled, (state, action) => {
                state.loading = false;
                // Handle both array (old fallback) and object with employees/totalCount return
                // Cast payload to any to avoid TypeScript inference issues with complex union types in thunks
                const payload = action.payload as any;

                if (payload && 'employees' in payload) {
                    state.employees = payload.employees;
                    state.totalCount = payload.totalCount || payload.employees.length;
                    state.loadedPage = payload.page || 1;

                    // Update server-side counts only if we are not currently filtering or searching.
                    // This prevents the dropdown options from collapsing to just the selected item.
                    const isSearching = !!action.meta.arg?.search;
                    const isFilteringStatus = action.meta.arg?.is_active !== undefined;
                    const isFilteringRole = !!action.meta.arg?.role;

                    if (payload.statusCounts && !isSearching && !isFilteringStatus) {
                        state.statusCounts = payload.statusCounts;
                    }
                    if (payload.roleCounts && !isSearching && !isFilteringRole) {
                        state.roleCounts = payload.roleCounts;
                    }
                } else if (Array.isArray(payload)) {
                    state.employees = payload;
                    state.totalCount = payload.length;
                } else {
                    // Fallback
                    state.employees = [];
                    state.totalCount = 0;
                }
            })
            .addCase(fetchEmployees.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Create Employee
            .addCase(createEmployee.pending, (state) => {
                state.createLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(createEmployee.fulfilled, (state, action) => {
                state.createLoading = false;
                state.successMessage = action.payload?.message || 'Employee created successfully';
            })
            .addCase(createEmployee.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload as string;
            })
            // Delete Employee
            .addCase(deleteEmployee.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(deleteEmployee.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.successMessage = action.payload?.message || 'Employee deleted successfully';
            })
            .addCase(deleteEmployee.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload as string;
            })
            // Update Employee Status
            .addCase(updateEmployeeStatus.pending, (state, action) => {
                state.updateStatusLoading = action.meta.arg.mobile;
                state.error = null;
            })
            .addCase(updateEmployeeStatus.fulfilled, (state, action) => {
                state.updateStatusLoading = null;
                state.successMessage = `Employee ${action.meta.arg.status ? 'activated' : 'deactivated'} successfully`;

                // Update role_status in both employees lists
                const updateEmp = (emp: any) => {
                    if (emp.mobile !== action.meta.arg.mobile) return;
                    if (action.payload.role_status) {
                        emp.role_status = action.payload.role_status;
                    } else {
                        emp.role_status = { ...(emp.role_status || {}), [action.meta.arg.role.toUpperCase()]: action.meta.arg.status };
                    }
                };
                state.allEmployees.forEach(updateEmp);
                state.employees.forEach(updateEmp);
            })
            .addCase(updateEmployeeStatus.rejected, (state, action) => {
                state.updateStatusLoading = null;
                state.error = action.payload as string;
            })
            // Update Employee Assignment
            .addCase(updateEmployee.pending, (state) => {
                state.updateLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(updateEmployee.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.successMessage = action.payload?.message || 'Employee updated successfully';
            })
            .addCase(updateEmployee.rejected, (state, action) => {
                state.updateLoading = false;
                state.error = typeof action.payload === 'string' ? action.payload : JSON.stringify(action.payload);
            });
    },


});

export const { setEmployees, clearMessages, resetForRefresh, patchEmployeeScreenAccess } = employeesSlice.actions;

export const employeesReducer = employeesSlice.reducer;
export default employeesSlice.reducer;
